import { ArrowLeft, Trophy, Users, Play, Zap, Swords, UserPlus, Share2, Info, ChevronRight, Timer } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { getImageUrl } from '../utils/api';
import OptimizedImage from '../components/ui/OptimizedImage';

const Duel = ({ trackInteraction }) => {
    const navigate = useNavigate();
    const { fetchWithAuth } = useContext(AuthContext);
    const [gameMode, setGameMode] = useState('STANDARD');
    const [userInput, setUserInput] = useState('');
    const [gameId, setGameId] = useState(null);
    const [inviteToken, setInviteToken] = useState(null);
    const [joiningToken, setJoiningToken] = useState('');
    const [state, setState] = useState(null);

    // Initial check for join token in URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const join = params.get('join');
        const game = params.get('game');
        if (join) setJoiningToken(join);
        if (game) setGameId(game);
    }, []);

    const createGame = async () => {
        try {
            const resp = await fetchWithAuth('/api/duel/create/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ game_mode: gameMode })
            });
            let data;
            try {
                data = await resp.json();
            } catch (jsonError) {
                const text = await resp.text();
                console.error('Failed to parse JSON response:', text);
                alert(`Fehler beim Erstellen des Duels: Server hat ungültige Antwort zurückgegeben (${resp.status}). Bitte versuche es erneut.`);
                return;
            }
            if (resp.ok) {
                setGameId(data.game_id);
                setInviteToken(data.invite_token);
                // Track Duel-Start
                if (trackInteraction) {
                    trackInteraction('DUEL_START', {
                        game_id: data.game_id,
                        action: 'created',
                        mode: gameMode
                    });
                }
            } else {
                const errorMsg = data?.error || data?.detail || `HTTP error! status: ${resp.status}`;
                console.error('Duel creation failed:', errorMsg);
                alert(`Fehler beim Erstellen des Duels: ${errorMsg}`);
            }
        } catch (err) {
            console.error('Error creating game:', err);
            alert(`Fehler beim Erstellen des Duels: ${err.message || 'Unbekannter Fehler'}`);
        }
    };

    const joinWithToken = async () => {
        if (!joiningToken) return;
        try {
            const resp = await fetchWithAuth(`/api/duel/join/${joiningToken}/`, { method: 'POST' });
            let data;
            try {
                data = await resp.json();
            } catch (jsonError) {
                const text = await resp.text();
                console.error('Failed to parse JSON response:', text);
                alert(`Fehler beim Beitreten zum Duell: Server hat ungültige Antwort zurückgegeben (${resp.status}).`);
                return;
            }
            if (resp.ok) {
                setGameId(data.game_id);
                // Track Duel-Start (Join)
                if (trackInteraction) {
                    trackInteraction('DUEL_START', {
                        game_id: data.game_id,
                        action: 'joined',
                        invite_token: joiningToken
                    });
                }
            } else {
                const errorMsg = data?.error || data?.detail || `HTTP error! status: ${resp.status}`;
                console.error('Duel join failed:', errorMsg);
                alert(`Fehler beim Beitreten zum Duell: ${errorMsg}`);
            }
        } catch (err) {
            console.error('Error joining game:', err);
            alert(`Fehler beim Beitreten zum Duell: ${err.message || 'Unbekannter Fehler'}`);
        }
    };

    const poll = async () => {
        if (!gameId) return;
        try {
            const resp = await fetchWithAuth(`/api/duel/state/${gameId}/`);
            if (!resp.ok) {
                console.error('Poll failed:', resp.status, resp.statusText);
                return;
            }
            const contentType = resp.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await resp.text();
                console.error('Non-JSON response from poll:', text.substring(0, 100));
                return;
            }
            const data = await resp.json();
            setState(data);
        } catch (error) {
            console.error('Poll error:', error);
            // Nicht weiter propagieren, um Polling nicht zu stoppen
        }
    };

    useEffect(() => { if (gameId) { poll(); const t = setInterval(poll, 2000); return () => clearInterval(t); } }, [gameId]);

    const buzz = async () => {
        if (!gameId) return;
        await fetchWithAuth(`/api/duel/buzz/${gameId}/`, { method: 'POST' });
        poll();
        setUserInput('');
    };

    const submit = async (round_no, choice_id = null, text_answer = null) => {
        if (!gameId || !state) return;
        const payload = { round_no };
        if (choice_id !== null) payload.choice_id = choice_id;
        if (text_answer !== null) payload.user_answer = text_answer;
        await fetchWithAuth(`/api/duel/answer/${gameId}/`, { method: 'POST', body: JSON.stringify(payload) });
        poll();
        if (trackInteraction) {
            trackInteraction('DUEL_ANSWER', { game_id: gameId, round_no: round_no });
        }
    };

    const handleShare = async () => {
        const shareUsers = prompt('Gebe User-IDs ein (kommagetrennt) oder drücke Enter für alle Freunde:');
        if (shareUsers === null) return;
        try {
            let recipientIds = [];
            if (shareUsers.trim()) {
                recipientIds = shareUsers.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
            } else {
                const friendsResp = await fetchWithAuth('/api/friendships/');
                if (friendsResp.ok) {
                    const friendsData = await friendsResp.json();
                    recipientIds = friendsData.friends?.map(f => f.id) || [];
                }
            }
            if (recipientIds.length === 0) { alert('Keine Empfänger ausgewählt'); return; }
            const resp = await fetchWithAuth(`/api/duel/${gameId}/share-result/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ recipient_ids: recipientIds })
            });
            if (resp.ok) {
                const data = await resp.json();
                alert(`Ergebnis wurde mit ${data.shared_count} Nutzer(n) geteilt!`);
            } else {
                alert('Fehler beim Teilen');
            }
        } catch (e) { console.error('Failed to share', e); alert('Fehler beim Teilen'); }
    };

    return (
        <div className="min-h-screen bg-[#f8fafc] dark:bg-[#0a0a0a] relative overflow-x-hidden">
            {/* Ambient Background */}
            <div className="fixed top-0 left-0 w-full h-full pointer-events-none z-0 overflow-hidden">
                <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] bg-plantlix-500/5 dark:bg-plantlix-500/10 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-500/5 dark:bg-emerald-500/10 rounded-full blur-[100px]" />
            </div>

            <div className="relative z-10 p-4 sm:p-6 md:p-10 max-w-5xl mx-auto pb-24">
                {/* Header Section */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/quiz')}
                            className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-500 hover:text-plantlix-500 transition-all shadow-sm active:scale-90"
                        >
                            <ArrowLeft className="w-6 h-6" />
                        </button>
                        <div>
                            <h1 className="text-3xl font-black text-gray-900 dark:text-white tracking-tighter flex items-center gap-3">
                                Pflanzenduell <Swords className="w-8 h-8 text-plantlix-500" />
                            </h1>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Multiplayer Battle</p>
                        </div>
                    </div>
                </div>

                {!gameId && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                        {/* Game Creation Card */}
                        <div className="bg-white/70 dark:bg-white/5 backdrop-blur-3xl rounded-[2.5rem] p-8 border border-white dark:border-white/10 shadow-2xl flex flex-col justify-between">
                            <div>
                                <div className="w-14 h-14 bg-plantlix-500/20 rounded-2xl flex items-center justify-center mb-6 shadow-xl shadow-plantlix-500/10">
                                    <Play className="w-8 h-8 text-plantlix-500 fill-plantlix-500" />
                                </div>
                                <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-2 italic">Battle starten</h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 font-medium">Wähle einen Modus und fordere deine Freunde heraus.</p>
                                
                                <div className="space-y-4">
                                    <label className="block text-[10px] font-black uppercase text-gray-400 tracking-[0.2em] ml-1">Spielmodus</label>
                                    <div className="grid grid-cols-1 gap-2">
                                        {[
                                            { id: 'STANDARD', name: 'Standard', icon: '🎯', desc: 'Multiple Choice' },
                                            { id: 'FAMILY', name: 'Familien', icon: '🌳', desc: 'Botanik-Taxonomie' },
                                            { id: 'PFLANZE_IN_SCHLAU', name: 'Profi-Eingabe', icon: '🌿', desc: 'Namen tippen' },
                                            { id: 'SPEED', name: 'Speed-Battle', icon: '⚡', desc: 'Schnell-Bilder' }
                                        ].map(mode => (
                                            <button
                                                key={mode.id}
                                                onClick={() => setGameMode(mode.id)}
                                                className={`flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left ${gameMode === mode.id ? 'bg-plantlix-500/10 border-plantlix-500 shadow-lg' : 'bg-white/50 dark:bg-black/20 border-gray-100 dark:border-white/5 hover:border-plantlix-500/30'}`}
                                            >
                                                <span className="text-2xl">{mode.icon}</span>
                                                <div>
                                                    <p className={`font-black leading-none mb-1 ${gameMode === mode.id ? 'text-plantlix-600 dark:text-plantlix-400' : 'text-gray-900 dark:text-white'}`}>{mode.name}</p>
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{mode.desc}</p>
                                                </div>
                                                {gameMode === mode.id && <Zap className="w-5 h-5 text-plantlix-500 fill-plantlix-500 ml-auto" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            
                            <button onClick={createGame} className="mt-8 w-full py-5 rounded-3xl bg-plantlix-600 hover:bg-plantlix-500 text-white font-black text-xl shadow-2xl shadow-plantlix-600/30 transition-all active:scale-95 flex items-center justify-center gap-3 italic">
                                DUEL ERSTELLEN
                            </button>
                        </div>

                        {/* Join Card */}
                        <div className="bg-white/70 dark:bg-white/5 backdrop-blur-3xl rounded-[2.5rem] p-8 border border-white dark:border-white/10 shadow-2xl flex flex-col justify-between">
                            <div>
                                <div className="w-14 h-14 bg-amber-500/20 rounded-2xl flex items-center justify-center mb-6 shadow-xl shadow-amber-500/10">
                                    <UserPlus className="w-8 h-8 text-amber-500" />
                                </div>
                                <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-2 italic">Beitreten</h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 font-medium">Gib den Code deines Gegners ein um das Duel zu starten.</p>
                                
                                <div className="space-y-4">
                                    <label className="block text-[10px] font-black uppercase text-gray-400 tracking-[0.2em] ml-1">Einladungscode</label>
                                    <input 
                                        value={joiningToken} 
                                        onChange={e => setJoiningToken(e.target.value)} 
                                        placeholder="CODE EINGEBEN" 
                                        className="w-full p-5 bg-white/50 dark:bg-black/20 border-2 border-gray-100 dark:border-white/10 rounded-3xl text-center text-2xl font-black text-plantlix-600 placeholder-gray-300 dark:placeholder-white/5 focus:border-plantlix-500 outline-none transition-all uppercase" 
                                    />
                                </div>
                            </div>
                            
                            <button onClick={joinWithToken} className="mt-8 w-full py-5 rounded-3xl bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white font-black text-xl shadow-xl transition-all active:scale-95 flex items-center justify-center gap-3 italic">
                                JETZT BEITRETEN
                            </button>
                        </div>
                    </div>
                )}

                {gameId && inviteToken && state?.status === 'WAITING' && (
                    <div className="bg-white/70 dark:bg-white/5 backdrop-blur-3xl rounded-[2.5rem] p-10 border border-white dark:border-white/10 shadow-2xl text-center animate-in zoom-in duration-500">
                        <div className="w-24 h-24 bg-plantlix-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Users className="w-12 h-12 text-plantlix-500" />
                        </div>
                        <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-2 italic">Warten auf Gegner...</h2>
                        <p className="text-gray-500 dark:text-gray-400 mb-10 font-medium">Teile diesen Code mit deinem Gegner, damit er dem Spiel beitreten kann.</p>
                        
                        <div className="inline-flex flex-col items-center gap-4">
                            <div className="px-10 py-6 bg-plantlix-500/10 border-2 border-plantlix-500 rounded-[2rem] shadow-2xl">
                                <span className="text-5xl font-black text-plantlix-600 dark:text-plantlix-400 tracking-[0.2em] font-mono">{inviteToken}</span>
                            </div>
                            <button 
                                onClick={() => {
                                    navigator.clipboard.writeText(inviteToken);
                                    alert('Code kopiert!');
                                }}
                                className="flex items-center gap-2 text-[10px] font-black uppercase text-plantlix-500 tracking-[0.2em] hover:text-plantlix-400 transition-colors"
                            >
                                <Share2 className="w-4 h-4" /> Code kopieren
                            </button>
                        </div>
                    </div>
                )}
                {state && state.status !== 'WAITING' && (
                    <div className="space-y-6 animate-in fade-in duration-500">
                        {/* Score Board */}
                        <div className="grid grid-cols-3 gap-4 items-center bg-white/70 dark:bg-white/5 backdrop-blur-3xl rounded-[2.5rem] p-8 border border-white dark:border-white/10 shadow-2xl">
                            <div className="text-center">
                                <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-2">Spieler A</p>
                                <div className={`text-5xl font-black italic tracking-tighter ${state.you_are === 'A' ? 'text-plantlix-500' : 'text-gray-900 dark:text-white'}`}>{state.a_score}</div>
                                {state.you_are === 'A' && <span className="text-[8px] font-black uppercase text-plantlix-500 tracking-widest">Das bist du</span>}
                            </div>
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-12 h-12 bg-black/5 dark:bg-white/5 rounded-full flex items-center justify-center">
                                    <Swords className="w-6 h-6 text-gray-400" />
                                </div>
                                <div className="flex items-center gap-2 px-3 py-1 bg-plantlix-500/10 border border-plantlix-500/20 rounded-full">
                                    <Timer className={`w-3 h-3 ${state.remaining_ms < 5000 ? 'text-red-500 animate-pulse' : 'text-plantlix-500'}`} />
                                    <span className={`text-xs font-black tabular-nums ${state.remaining_ms < 5000 ? 'text-red-500' : 'text-plantlix-500'}`}>
                                        {Math.ceil((state.remaining_ms || 0) / 1000)}s
                                    </span>
                                </div>
                            </div>
                            <div className="text-center">
                                <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-2">Spieler B</p>
                                <div className={`text-5xl font-black italic tracking-tighter ${state.you_are === 'B' ? 'text-plantlix-500' : 'text-gray-900 dark:text-white'}`}>{state.b_score}</div>
                                {state.you_are === 'B' && <span className="text-[8px] font-black uppercase text-plantlix-500 tracking-widest">Das bist du</span>}
                            </div>
                        </div>

                        {/* Game Status Message */}
                        {state.status === 'FINISHED' && (
                            <div className="bg-gradient-to-br from-plantlix-600 to-emerald-800 rounded-[2.5rem] p-10 text-center text-white shadow-2xl relative overflow-hidden group">
                                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10 pointer-events-none" />
                                <div className="relative z-10">
                                    <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-xl border border-white/30 shadow-2xl">
                                        <Trophy className="w-12 h-12 text-white fill-white animate-bounce" />
                                    </div>
                                    <h3 className="text-5xl font-black mb-2 italic tracking-tighter">BATTLE ENDE!</h3>
                                    <p className="text-xl font-bold opacity-80 mb-8">
                                        {state.a_score > state.b_score ? (state.you_are === 'A' ? "LEGENDÄR! DU HAST GEWONNEN! 🏆" : "SPIELER A TRIUMPHIERT!") :
                                            state.b_score > state.a_score ? (state.you_are === 'B' ? "LEGENDÄR! DU HAST GEWONNEN! 🏆" : "SPIELER B TRIUMPHIERT!") :
                                                "UNENTSCHIEDEN! 🤝"}
                                    </p>
                                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                        <button onClick={handleShare} className="px-10 py-5 bg-white text-plantlix-900 rounded-3xl font-black text-xl shadow-2xl hover:bg-plantlix-50 transition-all active:scale-95 flex items-center gap-3 italic">
                                            <Share2 className="w-6 h-6" /> ERGEBNIS TEILEN
                                        </button>
                                        <button onClick={() => window.location.reload()} className="px-10 py-5 bg-white/10 backdrop-blur-xl border border-white/20 text-white rounded-3xl font-black text-xl hover:bg-white/20 transition-all active:scale-95 italic">
                                            NEUES SPIEL
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!state.buzz_user && state.status === 'IN_PROGRESS' && (
                            <div className="py-10">
                                <button onClick={buzz} className="w-full py-12 bg-rose-600 hover:bg-rose-500 text-white rounded-[3rem] font-black text-6xl shadow-2xl shadow-rose-600/40 border-b-[12px] border-rose-800 active:scale-95 active:border-b-0 transition-all transform hover:-translate-y-2 italic tracking-tighter">
                                    🚨 BUZZ!
                                </button>
                                <p className="text-center mt-6 text-[10px] font-black uppercase text-gray-400 tracking-[0.4em]">Hau drauf wenn du es weißt!</p>
                            </div>
                        )}

                        {state.rounds && state.rounds.map(r => (
                            <div key={r.round_no} className="bg-white/70 dark:bg-white/5 backdrop-blur-3xl rounded-[2.5rem] overflow-hidden border border-white dark:border-white/10 shadow-2xl animate-in slide-in-from-bottom-4 duration-500">
                                <div className="px-8 py-4 bg-black/5 dark:bg-white/5 border-b border-white dark:border-white/10 flex justify-between items-center">
                                    <span className="font-black text-xs uppercase tracking-widest text-gray-500">Runde {r.round_no}</span>
                                    {r.player_a_choice && r.player_b_choice && <span className="px-3 py-1 bg-plantlix-500 text-white text-[10px] font-black rounded-full uppercase tracking-widest animate-pulse">Abgeschlossen</span>}
                                </div>

                                <div className="p-8">
                                    {state.game_mode === 'PFLANZE_IN_SCHLAU' ? (
                                        <div className="space-y-8">
                                            <div className="text-center">
                                                <p className="text-[10px] font-black uppercase text-plantlix-500 tracking-widest mb-2">Wie lautet der botanische Name?</p>
                                                <h2 className="text-4xl font-black text-gray-900 dark:text-white italic tracking-tighter leading-none mb-6">{r.plant.german_name}</h2>
                                            </div>

                                            <div className="w-full max-w-2xl mx-auto bg-gray-900 rounded-[2rem] overflow-hidden shadow-2xl border border-white/10 aspect-video relative group">
                                                {r.plant?.images && r.plant.images.length > 0 ? (
                                                    <OptimizedImage
                                                        src={r.plant.images[0].image_medium || r.plant.images[0].image}
                                                        alt="Plant"
                                                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                                    />
                                                ) : (
                                                    <div className="flex items-center justify-center h-full text-gray-600 font-black uppercase tracking-widest">📷 Bild fehlt</div>
                                                )}
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                                            </div>

                                            <div className="relative group max-w-xl mx-auto">
                                                <input
                                                    type="text"
                                                    value={userInput}
                                                    onChange={(e) => setUserInput(e.target.value)}
                                                    placeholder="Lösung eintippen..."
                                                    disabled={!!(r.player_a_choice || r.player_b_choice) || (state.buzz_user_id !== null && state.buzz_user_id !== state.you_user_id) && !r.steal_deadline_at}
                                                    className="w-full p-6 bg-white/50 dark:bg-black/40 border-2 border-gray-100 dark:border-white/10 rounded-3xl text-center text-2xl font-black text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-white/5 focus:border-plantlix-500 outline-none transition-all"
                                                />
                                                <button
                                                    onClick={() => submit(r.round_no, null, userInput)}
                                                    disabled={!userInput.trim()}
                                                    className="absolute right-3 top-3 bottom-3 aspect-square bg-plantlix-600 hover:bg-plantlix-500 text-white rounded-2xl flex items-center justify-center shadow-xl transition-all active:scale-90"
                                                >
                                                    <Play className="w-6 h-6 fill-current" />
                                                </button>
                                            </div>
                                        </div>
                                    ) : state.game_mode === 'SPEED' ? (
                                        <div className="space-y-8">
                                            <div className="text-center">
                                                <p className="text-[10px] font-black uppercase text-plantlix-500 tracking-widest mb-2">Finde das passende Bild</p>
                                                <h2 className="text-4xl font-black text-gray-900 dark:text-white italic tracking-tighter leading-none mb-1">
                                                    {r.plant.german_name}
                                                </h2>
                                                <p className="text-lg font-bold text-gray-400 italic">
                                                    {r.plant.botanical_name}
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
                                                {r.options.map(opt => (
                                                    <button
                                                        key={opt.id}
                                                        onClick={() => submit(r.round_no, opt.id)}
                                                        disabled={r.player_a_choice || r.player_b_choice}
                                                        className={`relative aspect-square rounded-[2rem] overflow-hidden border-4 transition-all duration-300 group shadow-xl bg-gray-900
                                                            ${r.player_a_choice || r.player_b_choice ? 'opacity-40 grayscale cursor-not-allowed' : 'hover:scale-[1.05] border-white/10 hover:border-plantlix-500 shadow-plantlix-500/10'}
                                                        `}
                                                    >
                                                        <OptimizedImage 
                                                            src={opt.image_medium || opt.image} 
                                                            alt="" 
                                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" 
                                                        />
                                                        <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors" />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-8">
                                            <div className="text-center">
                                                <p className="text-[10px] font-black uppercase text-plantlix-500 tracking-widest mb-4">Wähle die richtige Antwort</p>
                                                <div className="w-full max-w-2xl mx-auto bg-gray-900 rounded-[2rem] overflow-hidden shadow-2xl border border-white/10 aspect-video mb-8 relative group">
                                                     <OptimizedImage
                                                        src={r.plant?.images?.[0]?.image_medium || r.plant?.images?.[0]?.image}
                                                        alt="Plant"
                                                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
                                                {r.options.map(opt => (
                                                    <button
                                                        key={opt.id}
                                                        onClick={() => submit(r.round_no, opt.id)}
                                                        disabled={r.player_a_choice || r.player_b_choice}
                                                        className={`p-6 rounded-3xl border-2 transition-all text-center relative overflow-hidden group active:scale-95 disabled:opacity-40
                                                            ${state.game_mode === 'FAMILY' ? 'py-8' : ''}
                                                            bg-white/50 dark:bg-white/5 border-white dark:border-white/10 hover:border-plantlix-500 hover:bg-plantlix-500/10 shadow-xl
                                                        `}
                                                    >
                                                        <span className={`font-black text-xl italic tracking-tight ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                            {state.game_mode === 'FAMILY' ? opt.text : opt.botanical_name}
                                                        </span>
                                                        {state.game_mode !== 'FAMILY' && (
                                                            <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Auswählen</span>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {(r.player_a_choice || r.player_b_choice) && (
                                        <div className="mt-8 flex items-center justify-center gap-2 py-3 bg-black/5 dark:bg-white/5 rounded-2xl">
                                            <Info className="w-4 h-4 text-gray-400" />
                                            <span className="text-[10px] font-black uppercase text-gray-500 tracking-widest">
                                                {r.player_a_choice && r.player_b_choice ? "Runde abgeschlossen - Analyse läuft..." : "Ein Spieler hat bereits geantwortet..."}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Duel;
